import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';

interface LiveTournament {
  id: number;
  name: string;
  format: string;
  year: number;
  status: string;
  start_date: string;
  end_date: string;
  description?: string;
  country?: string;
  organizer?: string;
}

interface LiveTournamentListProps {
  onSelect: (tournament: LiveTournament) => void;
  isAdmin?: boolean;
}

// Helper function to format date (YYYY-MM-DD) to a readable format
const formatDate = (dateString: string) => {
  if (!dateString) return 'TBD';
  // Extract just the date part if it includes time
  const dateOnly = dateString.split('T')[0];
  const date = new Date(dateOnly);
  return date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  });
};

const LiveTournamentList: React.FC<LiveTournamentListProps> = ({ onSelect, isAdmin }) => {
  const [liveTournaments, setLiveTournaments] = useState<LiveTournament[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTournaments = async () => {
      setLoading(true);
      try {
        const data = await apiService.getLiveTournaments();
        if (data.success) {
          setLiveTournaments(data.liveTournaments || []);
        } else {
          setError('Failed to load live tournaments');
        }
      } catch (err) {
        console.error('Error fetching live tournaments:', err);
        setError('Failed to load live tournaments');
      } finally {
        setLoading(false);
      }
    };
    fetchTournaments();
  }, []);

  const handleScheduleClick = (e: React.MouseEvent, tournament: LiveTournament) => {
    e.stopPropagation();
    navigate(`/tournament/${tournament.id}`);
  };

  if (loading) return <div className="text-center py-8 text-white">Loading live tournaments...</div>;
  if (error) return <div className="text-center py-8 text-red-400">{error}</div>;

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold text-white mb-1">Select a Live Tournament</h2>
      <p className="text-white/60 text-sm mb-4">Click a tournament row to view its fantasy leagues.</p>
      {liveTournaments.length === 0 ? (
        <div className="text-white/80">No live tournaments available.</div>
      ) : (
        <ul className="divide-y divide-white/10 bg-white/5 rounded-lg">
          {liveTournaments.map(t => (
            <li
              key={t.id}
              className="p-4 hover:bg-blue-900/30 cursor-pointer flex flex-col md:flex-row md:items-center md:justify-between"
              onClick={() => onSelect(t)}
            >
              <div>
                <div className="text-lg font-semibold text-white">{t.name} <span className="text-xs text-white/60">({t.format} {t.year})</span></div>
                <div className="text-white/60 text-sm">{t.status} | {formatDate(t.start_date)} - {formatDate(t.end_date)}</div>
                {t.description && <div className="text-white/40 text-xs mt-1">{t.description}</div>}
              </div>
              <div className="mt-2 md:mt-0 flex space-x-2">
                <button 
                  onClick={(e) => handleScheduleClick(e, t)}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  Schedule
                </button>
                {isAdmin && (
                  <button
                    onClick={e => { e.stopPropagation(); navigate(`/admin/tournament/${t.id}`); }}
                    className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    Admin Details
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LiveTournamentList; 